import type { IHttpError } from './httpClient';
import {
  STEP_UP_CANCELLED_CODE,
  STEP_UP_FAILED_CODE,
  STEP_UP_POPUP_BLOCKED_CODE,
  STEP_UP_REQUIRED_CODE,
} from './constants';
import type { StepUpRequiredDetails } from './types';

// The Wix applicative-error envelope unified error handling reads. `errorCode` is
// duplicated at the top of `data` (mirroring the real wire shape — e.g. a
// PERMISSION_DENIED body carries a top-level `errorCode`) so callers that read
// `error.response.data.errorCode` see it too; extractErrorCode reads the nested
// `applicationError.code`.
interface PlatformErrorEnvelope {
  data: {
    errorCode: string;
    details: {
      applicationError: { code: string; description: string; data?: unknown };
    };
  };
}

const platformEnvelope = (
  code: string,
  description: string,
  data?: unknown,
): PlatformErrorEnvelope => ({
  data: {
    errorCode: code,
    details: { applicationError: { code, description, data } },
  },
});

// Reads the applicative error code out of the two Wix wire shapes
// (`response.details` and `response.data.details`), then a couple of flatter
// fallbacks, then the error itself. Ported so a StepUp*Error is indistinguishable
// from a server applicative error to a caller's unified error handling.
//
// Note the nested `applicationError.code` is probed BEFORE the flat
// `response.data.errorCode`, so a top-level numeric `errorCode` (e.g. -100 on a
// PERMISSION_DENIED body) is never mistaken for the step-up signal.
export const extractErrorCode = (error: any): any =>
  error?.response?.details?.applicationError?.code ??
  error?.response?.data?.details?.applicationError?.code ??
  error?.response?.data?.errorCode ??
  error?.data?.errorCode ??
  error?.data?.reason ??
  error;

// Thrown when the user dismisses the step-up popup (closed / timed out without a
// token). Platform-shaped under a distinct STEP_UP_CANCELLED code so callers can
// flow it through unified error handling and still treat cancellation quietly.
export class StepUpCancelledError extends Error {
  readonly errorCode = STEP_UP_CANCELLED_CODE;
  readonly response: PlatformErrorEnvelope;

  constructor(message = 'Step-up authentication was cancelled') {
    super(message);
    this.name = 'StepUpCancelledError';
    this.response = platformEnvelope(STEP_UP_CANCELLED_CODE, message);
    Object.setPrototypeOf(this, StepUpCancelledError.prototype);
  }
}

// Thrown when step-up could not be completed: the popup reported a failure (e.g. no
// enrolled AMR sufficient for the required ACR), was blocked, or a replay with a
// fresh token still failed. Platform-shaped so it flows through unified error
// handling exactly like a server applicative error. Pass STEP_UP_POPUP_BLOCKED_CODE
// as the code for a blocked popup (recoverable by the user).
export class StepUpFailedError extends Error {
  readonly errorCode: string;
  readonly response: PlatformErrorEnvelope;

  constructor(
    errorCode?: string,
    message = 'Step-up authentication failed',
    data?: unknown,
  ) {
    super(message);
    this.name = 'StepUpFailedError';
    this.errorCode = errorCode || STEP_UP_FAILED_CODE;
    this.response = platformEnvelope(this.errorCode, message, data);
    Object.setPrototypeOf(this, StepUpFailedError.prototype);
  }
}

// True when the error is the identity guard's STEP_UP_REQUIRED. STATUS-AGNOSTIC: we
// key off the applicative code, not the HTTP status (403 is observed, but the same
// guard has surfaced 401 elsewhere), and never off the top-level numeric errorCode.
// Reuses `extractErrorCode`, which probes both envelope shapes.
export const isStepUpRequiredError = (error: unknown): error is IHttpError =>
  extractErrorCode(error) === STEP_UP_REQUIRED_CODE;

// True when step-up could not even start because the browser refused to open the
// popup (popup blocker / no user activation left). Recoverable by the user, so
// callers can surface a dedicated "allow popups / try again" treatment.
export const isStepUpPopupBlockedError = (error: unknown): boolean =>
  extractErrorCode(error) === STEP_UP_POPUP_BLOCKED_CODE;

// applicationError.data sits under response.details or response.data.details
// depending on the wire shape — the same two paths extractErrorCode probes.
const getApplicationErrorData = (error: any): any =>
  error?.response?.details?.applicationError?.data ??
  error?.response?.data?.details?.applicationError?.data ??
  {};

// Reads acr_values / max_age from the error payload. The proto is snake_case
// (`acr_values`, `max_age`); Wix REST serialization camelCases (`acrValues`,
// `maxAge`) and may send max_age as a STRING. Both casings are accepted and maxAge
// is coerced to a number.
export const extractStepUpDetails = (error: unknown): StepUpRequiredDetails => {
  const data = getApplicationErrorData(error);
  const acrValues: string[] = data.acrValues ?? data.acr_values ?? [];
  const rawMaxAge = data.maxAge ?? data.max_age;
  const maxAge =
    rawMaxAge === undefined || rawMaxAge === null
      ? undefined
      : Number(rawMaxAge);

  return { acrValues, maxAge };
};
